import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { StatusDot } from '@/components/StatusDot';
import { EmptyState } from '@/components/EmptyState';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ListChecks, Search, CheckCircle2, XCircle, Clock, DollarSign, Activity } from 'lucide-react';
import { relativeTime, shortId, truncate } from '@/lib/utils';

const WINDOW_OPTIONS = [
  { value: 1,   label: 'Last 1h' },
  { value: 24,  label: 'Last 24h' },
  { value: 168, label: 'Last 7d' },
  { value: 720, label: 'Last 30d' },
];

export default function Tasks() {
  const nav = useNavigate();
  const [status, setStatus] = React.useState('all');
  const [kind, setKind] = React.useState('all');
  const [windowH, setWindowH] = React.useState(24);
  const [search, setSearch] = React.useState('');

  const params = React.useMemo(() => {
    const p = { since_hours: windowH };
    if (status !== 'all') p.status = status;
    if (kind !== 'all') p.kind = kind;
    return p;
  }, [status, kind, windowH]);

  const { data, isLoading } = useQuery({
    queryKey: ['tasks', params],
    queryFn: () => api.get('/tasks', { params }).then(r => r.data),
    refetchInterval: 10000,
  });

  const { data: stats } = useQuery({
    queryKey: ['task-stats', windowH],
    queryFn: () => api.get('/tasks/stats', { params: { hours: windowH } }).then(r => r.data),
    refetchInterval: 15000,
  });

  const items = React.useMemo(() => {
    let arr = data?.items || [];
    if (search) arr = arr.filter((t) => (t.instruction + ' ' + (t.target_url || '') + ' ' + (t.agent_name || '')).toLowerCase().includes(search.toLowerCase()));
    return arr;
  }, [data, search]);

  const kinds = React.useMemo(() => {
    const keys = Object.keys(stats?.by_kind || {});
    return ['all', ...keys.sort()];
  }, [stats]);

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <PageHeader
        eyebrow="Execution"
        title="Tasks"
        description="Every agent action is durably tracked from enqueue to completion."
      />

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5" data-testid="tasks-stats">
        <StatCard label="Total" value={stats?.total ?? '—'} icon={Activity} />
        <StatCard label="Passed" value={stats?.pass_fail?.passed ?? '—'} icon={CheckCircle2} accent="success" />
        <StatCard label="Failed" value={stats?.pass_fail?.failed ?? '—'} icon={XCircle} accent="danger" />
        <StatCard label="Pass rate" value={stats?.pass_rate != null ? `${Math.round((stats.pass_rate || 0) * 100)}%` : '—'} icon={CheckCircle2} accent={stats?.pass_rate >= 0.7 ? 'success' : stats?.pass_rate >= 0.4 ? 'warning' : 'danger'} />
        <StatCard label="Cost" value={stats?.total_cost_usd != null ? `$${(stats.total_cost_usd || 0).toFixed(2)}` : '—'} icon={DollarSign} />
      </div>

      {/* Per-agent leaderboard */}
      {stats?.per_agent?.length > 0 && (
        <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))]" data-testid="tasks-per-agent">
          <div className="border-b border-white/5 px-4 py-2.5 text-xs uppercase tracking-wider text-[hsl(var(--text-3))]">
            Top agents · {WINDOW_OPTIONS.find(o => o.value === windowH)?.label.toLowerCase()}
          </div>
          <div className="divide-y divide-white/5">
            {stats.per_agent.slice(0, 5).map((a) => {
              const total = a.total || 1;
              const passPct = Math.round((a.completed / total) * 100);
              const failPct = Math.round((a.failed / total) * 100);
              return (
                <button
                  key={a.agent_id}
                  onClick={() => nav(`/agents/${a.agent_id}`)}
                  className="w-full px-4 py-2.5 flex items-center gap-3 text-left hover:bg-[hsl(var(--surface-2))] transition-colors"
                  data-testid={`tasks-per-agent-${a.agent_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{a.name}</div>
                    <div className="text-[11px] text-[hsl(var(--text-3))] font-mono">{shortId(a.agent_id)}</div>
                  </div>
                  <div className="flex items-center gap-4 text-xs">
                    <div className="text-[hsl(var(--success))]"><CheckCircle2 className="inline h-3 w-3 mr-1" />{a.completed}</div>
                    <div className="text-[hsl(var(--danger))]"><XCircle className="inline h-3 w-3 mr-1" />{a.failed}</div>
                    {a.running > 0 && <div className="text-[hsl(var(--warning))]"><Clock className="inline h-3 w-3 mr-1" />{a.running}</div>}
                    <div className="tabular text-[hsl(var(--text-3))]">${(a.cost_usd || 0).toFixed(2)}</div>
                  </div>
                  <div className="w-32 h-1.5 rounded-full overflow-hidden bg-[hsl(var(--surface-3))] shrink-0 flex">
                    <div className="bg-[hsl(var(--success))]" style={{ width: `${passPct}%` }} />
                    <div className="bg-[hsl(var(--danger))]" style={{ width: `${failPct}%` }} />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-[hsl(var(--text-3))]" />
          <Input placeholder="Search instruction / target / agent…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8" data-testid="tasks-search-input" />
        </div>
        <Select value={String(windowH)} onValueChange={(v) => setWindowH(Number(v))}>
          <SelectTrigger className="w-[140px]" data-testid="tasks-window-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {WINDOW_OPTIONS.map(o => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[160px]" data-testid="tasks-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="queued">Queued</SelectItem>
            <SelectItem value="running">Running</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="w-[170px]" data-testid="tasks-kind-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {kinds.map(k => <SelectItem key={k} value={k}>{k === 'all' ? 'All kinds' : k}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))]">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-[hsl(var(--text-3))]" data-testid="loading-skeleton">Loading tasks…</div>
        ) : items.length === 0 ? (
          <EmptyState icon={ListChecks} title="No tasks match" description="Submit a task from an agent's detail page, or widen the filters." />
        ) : (
          <Table data-testid="tasks-table">
            <TableHeader>
              <TableRow>
                <TableHead>Agent</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Verdict</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Cost</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((t) => (
                <TableRow key={t.id} className="cursor-pointer" onClick={() => nav(`/tasks/${t.id}`)} data-testid={`tasks-row-${t.id}`}>
                  <TableCell>
                    <div className="text-sm">{t.agent_name || '—'}</div>
                    <div className="font-mono text-[10px] text-[hsl(var(--text-3))]">{shortId(t.id)}</div>
                  </TableCell>
                  <TableCell><span className="rounded border border-white/10 bg-[hsl(var(--surface-2))] px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider">{t.kind}</span></TableCell>
                  <TableCell><StatusDot state={t.status} /></TableCell>
                  <TableCell>
                    {t.verdict ? (
                      <span className={`rounded border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${
                        ['REAL','LIKELY_REAL','posted','completed'].some(v => t.verdict?.toUpperCase().includes(v.toUpperCase())) ? 'border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]'
                        : ['failed','FAKE','error','unknown'].some(v => t.verdict?.toLowerCase().includes(v.toLowerCase())) ? 'border-[hsl(var(--danger))]/30 bg-[hsl(var(--danger))]/10 text-[hsl(var(--danger))]'
                        : 'border-white/10 bg-[hsl(var(--surface-2))] text-[hsl(var(--text-3))]'}`}>
                        {t.verdict}
                      </span>
                    ) : <span className="text-xs text-[hsl(var(--text-3))]">—</span>}
                  </TableCell>
                  <TableCell><span className="font-mono text-xs text-[hsl(var(--text-3))]">{t.target_url ? truncate(t.target_url, 40) : '—'}</span></TableCell>
                  <TableCell><span className="tabular text-xs text-[hsl(var(--text-3))]">{t.duration_sec != null ? formatDuration(t.duration_sec) : '—'}</span></TableCell>
                  <TableCell><span className="tabular text-sm">${(t.cost_usd || 0).toFixed(3)}</span></TableCell>
                  <TableCell><span className="text-xs text-[hsl(var(--text-3))]">{relativeTime(t.created_at)}</span></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}


function StatCard({ label, value, icon: Icon, accent = 'default' }) {
  const hue = accent === 'success' ? 'text-[hsl(var(--success))]'
    : accent === 'danger' ? 'text-[hsl(var(--danger))]'
    : accent === 'warning' ? 'text-[hsl(var(--warning))]'
    : 'text-[hsl(var(--text-1))]';
  return (
    <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))] px-4 py-3" data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--text-3))]">
        {Icon && <Icon className="h-3 w-3" />}
        <span>{label}</span>
      </div>
      <div className={`mt-1 text-2xl font-semibold tabular ${hue}`}>{value}</div>
    </div>
  );
}


function formatDuration(sec) {
  if (sec < 60) return `${sec.toFixed(1)}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${Math.floor(sec % 60)}s`;
  return `${(sec / 3600).toFixed(1)}h`;
}
